import React from 'react';
import { sb } from '../../modules/state';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/Card';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

const protocolNames = {
  '16:8': 'Daily Metabolic',
  '18:6': 'Accelerated Fat Burn',
  'OMAD': 'One Meal A Day',
  'TMAW': 'Two Meals A Week',
  'OMAW': 'One Meal A Week',
  'IRF': 'Insulin Reset Fast',
};

const thresholds = {
  '16:8': 960,   // 16 hours in minutes
  '18:6': 1080,  // 18 hours
  'OMAD': 1380,  // 23 hours
  'TMAW': 4320,  // 72 hours
  'OMAW': 7200,  // 120 hours
  'IRF': 0,
};

const otherReasons = ['Hunger', 'Medical', 'Social', 'Personal'];

export function ProtocolTimer() {
  const { currentUser } = useAuth();
  const [activeFast, setActiveFast] = React.useState(null);
  const [timerDisplay, setTimerDisplay] = React.useState({ days: 0, hours: 0, minutes: 0 });
  const [isStopModalOpen, setIsStopModalOpen] = React.useState(false);
  const [stopReason, setStopReason] = React.useState('Hunger');
  const [otherReasonText, setOtherReasonText] = React.useState('');
  const [currentProtocol, setCurrentProtocol] = React.useState('16:8');
  const [currentStreak, setCurrentStreak] = React.useState(0);
  const [bestStreak, setBestStreak] = React.useState(0);

  // Fetch state on mount - active fast, protocol, and streaks
  React.useEffect(() => {
    const fetchState = async () => {
      if (!currentUser?.id || !sb) return;

      try {
        // Get current protocol, current_streak, and best_streak from profile
        const { data: profileData } = await sb
          .from('profiles')
          .select('current_protocol, current_streak, best_streak')
          .eq('id', currentUser.id)
          .single();
        if (profileData?.current_protocol) {
          setCurrentProtocol(profileData.current_protocol);
        }
        if (profileData?.current_streak !== undefined) {
          setCurrentStreak(profileData.current_streak);
        }
        if (profileData?.best_streak !== undefined) {
          setBestStreak(profileData.best_streak);
        }

        // Get active fast
        const { data } = await sb
          .from('fasting_sessions')
          .select('*')
          .eq('user_id', currentUser.id)
          .eq('status', 'active')
          .single();
        setActiveFast(data);
      } catch (error) {
        console.error('[ProtocolTimer] Error fetching state:', error);
      }
    };

    fetchState();
  }, [currentUser]);

  // Timer calculation - runs every second
  React.useEffect(() => {
    if (!activeFast) {
      setTimerDisplay({ days: 0, hours: 0, minutes: 0 });
      return;
    }

    const calculateTimer = () => {
      const elapsed = Date.now() - new Date(activeFast.fast_start).getTime();
      const days = Math.floor(elapsed / 86400000);
      const hours = Math.floor((elapsed % 86400000) / 3600000);
      const minutes = Math.floor((elapsed % 3600000) / 60000);
      setTimerDisplay({ days, hours, minutes });
    };

    calculateTimer();
    const interval = setInterval(calculateTimer, 1000);
    return () => clearInterval(interval);
  }, [activeFast]);

  // Start fast
  const handleStart = async () => {
    if (!currentUser?.id || !sb) return;

    try {
      await sb.from('fasting_sessions').insert({
        user_id: currentUser.id,
        protocol: currentProtocol,
        fast_start: new Date().toISOString(),
        status: 'active',
      });
      // Refresh active fast state
      const { data } = await sb
        .from('fasting_sessions')
        .select('*')
        .eq('user_id', currentUser.id)
        .eq('status', 'active')
        .single();
      setActiveFast(data);
    } catch (error) {
      console.error('[ProtocolTimer] Error starting fast:', error);
    }
  };

  // Stop fast - opens modal
  const handleStopClick = () => {
    setIsStopModalOpen(true);
  };

  // Confirm stop
  const handleConfirmStop = async () => {
    if (!activeFast || !sb) {
      setIsStopModalOpen(false);
      return;
    }

    // Calculate elapsed minutes
    const elapsed = Date.now() - new Date(activeFast.fast_start).getTime();
    const elapsedMinutes = Math.floor(elapsed / 60000);

    // Determine if success or early stop
    const threshold = thresholds[currentProtocol];
    const isCompleted = elapsedMinutes >= threshold;

    try {
      // Use otherReasonText if reason is "Other", otherwise use stopReason
      const finalReason = stopReason === 'Other' && otherReasonText.trim() !== '' ? otherReasonText : stopReason;

      await sb.from('fasting_sessions').update({
        fast_end: new Date().toISOString(),
        status: isCompleted ? 'completed' : 'abandoned',
        reason: finalReason,
        duration_minutes: elapsedMinutes,
      }).eq('id', activeFast.id);

      // Update streak if daily protocol and completed
      if (currentProtocol !== 'IRF' && isCompleted) {
        await sb.from('profiles').increment('current_streak', 1).eq('id', currentUser.id);
        // Refresh streak from database
        const { data: profileData } = await sb
          .from('profiles')
          .select('current_streak, best_streak')
          .eq('id', currentUser.id)
          .single();
        if (profileData?.current_streak !== undefined) {
          setCurrentStreak(profileData.current_streak);
        }
        if (profileData?.best_streak !== undefined) {
          setBestStreak(profileData.best_streak);
        }
      }

      setActiveFast(null);
      setIsStopModalOpen(false);
    } catch (error) {
      console.error('[ProtocolTimer] Error stopping fast:', error);
    }
  };

  // Get left panel content based on protocol type
  const getLeftPanelContent = () => {
    const isDailyProtocol = ['16:8', '18:6', 'OMAD'].includes(currentProtocol);

    if (!activeFast) {
      return (
        <div className="flex flex-col gap-2">
          <div>
            <div className="text-2xl font-bold text-primary">{currentProtocol}</div>
            <div className="text-sm text-on-surface-variant">{protocolNames[currentProtocol]}</div>
          </div>
          <div className="text-xs text-on-surface-variant">
            {isDailyProtocol
              ? `Current Streak: ${currentStreak}`
              : 'Ready to start your fast'}
          </div>
        </div>
      );
    }

    if (isDailyProtocol) {
      return (
        <div className="flex flex-col gap-2">
          <div>
            <div className="text-2xl font-bold text-primary">{currentProtocol}</div>
            <div className="text-sm text-on-surface-variant">{protocolNames[currentProtocol]}</div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-xs text-on-surface-variant">Current Streak:</span>
            <span className="text-3xl font-black text-primary">{currentStreak}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-xs text-on-surface-variant">Best Streak:</span>
            <span className="text-3xl font-black text-primary">{bestStreak}</span>
          </div>
        </div>
      );
    }

    // Long protocols (TMAW, OMAW)
    const daysElapsed = Math.floor((Date.now() - new Date(activeFast.fast_start).getTime()) / 86400000);
    return (
      <div className="flex flex-col gap-2">
        <div>
          <div className="text-2xl font-bold text-primary">{currentProtocol}</div>
          <div className="text-sm text-on-surface-variant">{protocolNames[currentProtocol]}</div>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-xs text-on-surface-variant">Day:</span>
          <span className="text-3xl font-black text-primary">{daysElapsed + 1}</span>
          <span className="text-xs text-on-surface-variant">of fast</span>
        </div>
      </div>
    );
  };

  // Timer display format
  const formatTimer = () => {
    const parts = [];
    if (timerDisplay.days > 0) {
      parts.push(`${timerDisplay.days} Days`);
    }
    parts.push(`${timerDisplay.hours.toString().padStart(2, '0')} : ${timerDisplay.minutes.toString().padStart(2, '0')} Hrs Min`);
    return parts.join(' | ');
  };

  return (
    <Card className="bg-surface-container-low rounded-xl p-4 border border-outline">
      <div className="flex items-center justify-between">
        {/* Left Panel */}
        <div className="flex-1 min-w-0">
          {getLeftPanelContent()}
        </div>

        {/* Right Panel - Timer */}
        <div className="ml-6 min-w-[140px] text-right">
          <div className="font-black text-4xl text-primary mb-1">
            {timerDisplay.days.toString().padStart(2, '0')}
            <span className="text-2xl">:</span>
            {timerDisplay.hours.toString().padStart(2, '0')}
            <span className="text-2xl">:</span>
            {timerDisplay.minutes.toString().padStart(2, '0')}
          </div>
          <div className="flex items-center justify-end gap-3 text-xs text-on-surface-variant">
            <span>Days</span>
            <span>Hours</span>
            <span>Min</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-4 flex items-center justify-center gap-2">
        {activeFast ? (
          <button
            onClick={handleStopClick}
            className="flex-1 rounded-lg bg-error-container px-4 py-2 text-base font-medium text-error transition-colors hover:bg-error-container/80"
          >
            STOP
          </button>
        ) : (
          <Button
            variant="primary"
            onClick={handleStart}
          >
            START
          </Button>
        )}
      </div>

      {/* Stop Modal */}
      <Modal
        isOpen={isStopModalOpen}
        onClose={() => setIsStopModalOpen(false)}
        title="Are you ending your fast early?"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-2">
              Reason:
            </label>
            <select
              value={stopReason}
              onChange={(e) => {
                setStopReason(e.target.value);
                if (e.target.value !== 'Other') {
                  setOtherReasonText('');
                }
              }}
              className="w-full rounded-lg border border-outline bg-surface px-3 py-2 text-on-surface focus:border-primary focus:outline-none"
            >
              <option>Hunger</option>
              <option>Medical</option>
              <option>Social</option>
              <option>Personal</option>
              <option>Other</option>
            </select>
            {stopReason === 'Other' && (
              <input
                type="text"
                value={otherReasonText}
                onChange={(e) => setOtherReasonText(e.target.value)}
                placeholder="Please specify"
                className="mt-2 w-full rounded-lg border border-outline bg-surface px-3 py-2 text-on-surface focus:border-primary focus:outline-none"
              />
            )}
          </div>

          <div className="flex items-center justify-center gap-2">
            <Button variant="secondary" onClick={() => setIsStopModalOpen(false)}>
              Continue Fasting
            </Button>
            <Button variant="danger" onClick={handleConfirmStop}>
              Confirm Stop
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}

export default ProtocolTimer;
